import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Seo from '../../components/Seo';
import { useAsync } from '../../lib/useAsync';
import { approvalsApi } from '../../api/resources';
import { PageLoader, ErrorState, EmptyState, Spinner } from '../../components/ui';
import { relativeTime, formatDate, truncate } from '../../lib/format';
import SubmissionPreview from '../../components/SubmissionPreview';
import {
  SUBMISSION_TYPES,
  SUBMISSION_ORDER,
  normalizeSubmission,
  byNewest,
} from '../../components/submissionTypes';

const RESOURCE_META = {
  stories: { label: 'Stories', icon: '📰', to: '/admin/stories' },
  news: { label: 'News', icon: '🗞️', to: '/admin/news' },
  events: { label: 'Events', icon: '📅', to: '/admin/events' },
  activities: { label: 'Activities', icon: '🤝', to: '/admin/activities' },
  media: { label: 'Media', icon: '🖼️', to: '/admin/media' },
  pages: { label: 'Pages', icon: '🧩', to: '/admin/pages' },
  announcements: { label: 'Announcements', icon: '📢', to: '/admin/approvals' },
};

const FILTERS = [{ key: 'all', label: 'All' }, ...SUBMISSION_ORDER.map((k) => ({
  key: k,
  label: SUBMISSION_TYPES[k].plural,
}))];

export default function ApprovalCenter() {
  const pending = useAsync(() => approvalsApi.pending(), []);
  const recent = useAsync(() => approvalsApi.list({ per_page: 15 }), []);

  const [queue, setQueue] = useState({ loading: true, error: null, items: [], failures: [] });
  const [filter, setFilter] = useState('all');
  const [preview, setPreview] = useState(null);

  /**
   * Pull the pending Stories, News and Activities in parallel. A single type
   * failing (permissions, network) must not blank the whole queue, so partial
   * results are kept and the failures are reported inline.
   */
  const loadQueue = useCallback(async () => {
    setQueue((q) => ({ ...q, loading: true, error: null }));
    const results = await Promise.allSettled(
      SUBMISSION_ORDER.map((type) => SUBMISSION_TYPES[type].api.list(SUBMISSION_TYPES[type].listParams))
    );

    const items = [];
    const failures = [];
    results.forEach((res, i) => {
      const type = SUBMISSION_ORDER[i];
      if (res.status === 'fulfilled') {
        (res.value?.items || []).forEach((raw) => items.push(normalizeSubmission(raw, type)));
      } else {
        failures.push({
          type,
          message: res.reason?.message || `Could not load pending ${SUBMISSION_TYPES[type].plural}.`,
        });
      }
    });

    items.sort(byNewest);
    setQueue({
      loading: false,
      error: failures.length === SUBMISSION_ORDER.length ? failures[0] : null,
      items,
      failures,
    });
  }, []);

  useEffect(() => {
    loadQueue();
  }, [loadQueue]);

  const refreshAll = useCallback(() => {
    loadQueue();
    pending.reload();
    recent.reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loadQueue]);

  if (pending.loading) return <PageLoader />;
  if (pending.error) return <ErrorState error={pending.error} onRetry={pending.reload} />;

  const counts = pending.data?.data || {};
  const total = pending.data?.total ?? 0;

  const queueCounts = SUBMISSION_ORDER.reduce(
    (acc, type) => ({ ...acc, [type]: queue.items.filter((i) => i.type === type).length }),
    { all: queue.items.length }
  );
  const visible = filter === 'all' ? queue.items : queue.items.filter((i) => i.type === filter);

  return (
    <div className="space-y-8">
      <Seo title="Approval Center" />
      <div>
        <h1 className="font-display text-2xl font-bold">Approval Center</h1>
        <p className="text-ink-muted">
          {total} item{total === 1 ? '' : 's'} awaiting review across all content types.
        </p>
      </div>

      {/* Pending counts by content type */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 lg:grid-cols-7">
        {Object.entries(counts).map(([key, count]) => {
          const meta = RESOURCE_META[key] || { label: key, icon: '•', to: '/admin/approvals' };
          return (
            <Link key={key} to={meta.to} className="card card-hover p-4 text-center">
              <div className="mb-1 text-xl">{meta.icon}</div>
              <div className="font-display text-2xl font-bold text-maroon-700">{count}</div>
              <div className="text-xs text-ink-muted">{meta.label}</div>
            </Link>
          );
        })}
      </div>

      {/* Review queue */}
      <section className="card p-6">
        <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
          <div>
            <h2 className="font-display text-lg font-semibold text-ink">Review queue</h2>
            <p className="text-sm text-ink-muted">
              Open a submission to see the full content and design before you decide.
            </p>
          </div>
          <button onClick={loadQueue} className="btn-ghost text-sm" disabled={queue.loading}>
            {queue.loading ? 'Refreshing…' : 'Refresh'}
          </button>
        </div>

        <div className="mb-4 flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              onClick={() => setFilter(f.key)}
              aria-pressed={filter === f.key}
              className={`pill cursor-pointer transition-colors ${
                filter === f.key
                  ? 'bg-maroon-700 text-white'
                  : 'bg-surface-gray text-ink-soft hover:bg-cream-300'
              }`}
            >
              {f.label} ({queueCounts[f.key] ?? 0})
            </button>
          ))}
        </div>

        {queue.failures.length > 0 && queue.failures.length < SUBMISSION_ORDER.length && (
          <div className="mb-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
            {queue.failures.map((f) => (
              <p key={f.type}>{f.message}</p>
            ))}
          </div>
        )}

        {queue.loading ? (
          <div className="flex items-center gap-3 py-10 text-sm text-ink-muted">
            <Spinner className="h-5 w-5" />
            Loading pending submissions…
          </div>
        ) : queue.error ? (
          <ErrorState error={queue.error} onRetry={loadQueue} />
        ) : visible.length === 0 ? (
          <EmptyState
            title="Nothing waiting for review"
            message={
              filter === 'all'
                ? 'Every story, news post and activity has been reviewed. New submissions land here automatically.'
                : `No pending ${SUBMISSION_TYPES[filter].plural.toLowerCase()} right now.`
            }
          />
        ) : (
          <ul className="divide-y divide-cream-300">
            {visible.map((item) => (
              <li key={item.key}>
                <button
                  type="button"
                  onClick={() => setPreview(item)}
                  aria-label={`Preview ${item.title}`}
                  className="flex w-full items-center gap-4 rounded-xl px-2 py-3 text-left transition-colors hover:bg-surface-alt focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400"
                >
                  <Thumb item={item} />
                  <div className="min-w-0 flex-1">
                    <div className="mb-1 flex flex-wrap items-center gap-2">
                      <span className={`pill ${SUBMISSION_TYPES[item.type].badgeClass}`}>
                        {SUBMISSION_TYPES[item.type].label}
                      </span>
                      {item.category && <span className="pill-gold">{item.category}</span>}
                    </div>
                    <p className="truncate font-medium text-ink">{item.title}</p>
                    <p className="text-xs text-ink-muted">
                      {item.author ? `${item.author} · ` : ''}
                      {item.date ? `${item.dateLabel} ${formatDate(item.date)}` : 'No date'}
                    </p>
                    {item.excerpt && (
                      <p className="mt-1 hidden text-sm text-ink-muted sm:block">
                        {truncate(item.excerpt, 120)}
                      </p>
                    )}
                  </div>
                  <span className="btn-outline shrink-0 px-3 py-1.5 text-xs" aria-hidden="true">
                    Preview
                  </span>
                </button>
              </li>
            ))}
          </ul>
        )}
      </section>

      {/* Recent approval audit trail */}
      <section className="card p-6">
        <h2 className="mb-4 text-lg font-semibold text-ink">Recent Workflow Activity</h2>
        {recent.loading ? (
          <PageLoader />
        ) : recent.error ? (
          <ErrorState error={recent.error} onRetry={recent.reload} />
        ) : recent.data?.items?.length ? (
          <ul className="divide-y divide-cream-300 text-sm">
            {recent.data.items.map((ap) => (
              <li key={ap.id} className="flex items-start gap-3 py-3">
                <span className="pill-maroon capitalize">{ap.action}</span>
                <div className="min-w-0 flex-1">
                  <p className="text-ink-soft">
                    {ap.approvable_type} #{ap.approvable_id}: {ap.from_status} → {ap.to_status}
                  </p>
                  {ap.comment && <p className="text-xs italic text-ink-muted">“{ap.comment}”</p>}
                  <p className="text-xs text-ink-muted">
                    {ap.reviewer?.name ? `${ap.reviewer.name} · ` : ''}
                    {relativeTime(ap.created_at)}
                  </p>
                </div>
              </li>
            ))}
          </ul>
        ) : (
          <EmptyState title="No workflow activity yet" />
        )}
      </section>

      <SubmissionPreview
        item={preview}
        onClose={() => setPreview(null)}
        onRefresh={refreshAll}
      />
    </div>
  );
}

/** Small square thumbnail, or a type-colored placeholder when there is no image. */
function Thumb({ item }) {
  if (item.image) {
    return (
      <img
        src={item.image}
        alt=""
        aria-hidden="true"
        loading="lazy"
        className="h-14 w-14 shrink-0 rounded-lg object-cover ring-1 ring-cream-300"
      />
    );
  }
  return (
    <span
      aria-hidden="true"
      className="flex h-14 w-14 shrink-0 items-center justify-center rounded-lg bg-maroon-50 font-display text-lg font-bold text-maroon-700 ring-1 ring-cream-300"
    >
      {item.title?.charAt(0)?.toUpperCase() || '•'}
    </span>
  );
}
